import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import './ChatBot.css';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChatIcon, setShowChatIcon] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: 'Hi! I\'m your SBG assistant. How can I help you today?'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [lastContext, setLastContext] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we should show the chat icon based on the current page
  const shouldShowOnPath = () => {
    // Pages where we don't want to show the chatbot at all
    const HIDE_ON_PATHS = [
      '/admin-dashboard',
      '/admin/',
      '/employee-portal',
      '/employee/',
      '/login',
      '/register'
    ];
    
    // Don't show on admin or login pages
    if (HIDE_ON_PATHS.some(path => location.pathname.startsWith(path))) {
      return false;
    }
    
    // Pages where users typically need help (show chatbot with shorter delay)
    const HIGH_HELP_PAGES = [
      '/jobs',
      '/services',
      '/apply',
      '/employer-contact',
      '/general-application',
      '/faq'
    ];
    
    // If we're on a high-help page, show the chatbot with a shorter delay
    if (HIGH_HELP_PAGES.some(path => location.pathname === path || location.pathname.startsWith(path + '/'))) {
      return 'high-help';
    }
    
    // For all other pages, show with normal delay
    return 'normal';
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Add a delay before showing the chatbot icon based on page type
  useEffect(() => {
    // Only show if not previously dismissed
    if (!localStorage.getItem('hideChatBot')) {
      const pageType = shouldShowOnPath();
      
      if (pageType === false) {
        // Don't show on admin or login pages
        return;
      }
      
      // Set delay based on page type
      const delay = pageType === 'high-help' ? 5000 : 15000; // 5 seconds for high-help pages, 15 for others
      
      const timer = setTimeout(() => {
        setShowChatIcon(true);
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname]); // Re-run when path changes

  const knowledgeBase = {
    jobs: {
      keywords: ['job', 'jobs', 'position', 'career', 'work', 'employment', 'hire'],
      response: 'You can find all our job listings in the Jobs section. Would you like me to take you there?',
      action: () => navigate('/jobs'),
      pageName: 'jobs'
    },
    services: {
      keywords: ['service', 'services', 'offering', 'provide', 'help'],
      response: 'We offer various services including Staff Augmentation, Direct Hires, and Temporary Employment. Would you like to learn more?',
      action: () => navigate('/services'),
      pageName: 'services'
    },
    apply: {
      keywords: ['apply', 'application', 'submit', 'resume', 'cv'],
      response: 'You can submit a general application or apply for specific positions. Would you like to see the application form?',
      action: () => navigate('/general-application'),
      pageName: 'application'
    },
    contact: {
      keywords: ['contact', 'reach', 'email', 'phone', 'address'],
      response: 'You can find our contact information on the Contact page. Would you like me to show you?',
      action: () => navigate('/contact'),
      pageName: 'contact'
    }
  };

  const handleAffirmativeResponse = () => {
    if (!lastContext) return null;
    lastContext.action();
    return `Great! I'll take you to the ${lastContext.pageName} page right away!`;
  };

  const processMessage = (message) => {
    const lowerMessage = message.toLowerCase();
    
    // Handle yes/no responses
    if (['yes', 'yeah', 'sure', 'okay', 'ok', 'yep', 'please'].includes(lowerMessage)) {
      const affirmativeResponse = handleAffirmativeResponse();
      if (affirmativeResponse) return affirmativeResponse;
    }

    if (['no', 'nope', 'nah'].includes(lowerMessage)) {
      setLastContext(null);
      return "Alright, what else would you like to know about?";
    }

    // Check for navigation commands
    if (lowerMessage.includes('take me to') || lowerMessage.includes('go to')) {
      for (const [key, value] of Object.entries(knowledgeBase)) {
        if (value.keywords.some(keyword => lowerMessage.includes(keyword))) {
          value.action();
          return `I'll take you to the ${value.pageName} page right away!`;
        }
      }
    }

    // Check for questions/keywords
    for (const [key, value] of Object.entries(knowledgeBase)) {
      if (value.keywords.some(keyword => lowerMessage.includes(keyword))) {
        setLastContext(value);
        return value.response;
      }
    }

    // Default response
    setLastContext(null);
    return "I'm not sure about that. Would you like to know about our jobs, services, or how to apply?";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: inputValue
    };

    // Process the message and get bot response
    const botResponse = {
      type: 'bot',
      content: processMessage(inputValue)
    };

    setMessages(prev => [...prev, userMessage, botResponse]);
    setInputValue('');
  };

  const handleHideChatBot = () => {
    localStorage.setItem('hideChatBot', 'true');
    // Force a rerender of the parent component
    window.location.reload();
  };

  // Don't render if we're on an admin or employee portal page
  if (shouldShowOnPath() === false) {
    return null;
  }

  return (
    <div className="chatbot-container">
      {!isOpen && showChatIcon && (
        <button 
          className="chatbot-toggle"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat assistant"
          title="Chat with us"
        >
          <MessageCircle />
        </button>
      )}
      
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>SBG Assistant</h3>
            <div>
              <button 
                className="minimize-button"
                onClick={() => setIsOpen(false)}
                aria-label="Minimize chat"
                title="Minimize"
              >
                _
              </button>
              <button 
                className="close-button"
                onClick={handleHideChatBot}
                aria-label="Hide chat permanently"
                title="Hide permanently"
              >
                ×
              </button>
            </div>
          </div>
          
          <div className="messages-container">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.type}`}
              >
                {message.content}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="message-input"
            />
            <button type="submit" className="send-button">
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
